import { Anchor, Box, Breadcrumbs, Divider, Group, Menu, Text, Tooltip } from "@mantine/core";
import { useContext, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import HelpTourTarget from "src/components/help/HelpTourTarget";
import LayoutContext, { type ILayoutBreadcrumb } from "src/contexts/layouts/LayoutContext";
import { helpTourTargets } from "src/help/helpTours";

import BreadcrumbIcon from "../breadcrumbs/BreadcrumbIcon";

/**
 * Renders a truncated breadcrumb label with its identity icon and delayed full-label tooltip.
 */
const BreadcrumbContent = ({ breadcrumb }: { breadcrumb: ILayoutBreadcrumb }) => (
    <Tooltip
        label={breadcrumb.label}
        disabled={!breadcrumb.label}
        openDelay={500}
        withArrow
    >
        <Group
            gap={6}
            wrap="nowrap"
            maw={260}
        >
            <BreadcrumbIcon
                icon={breadcrumb.icon}
                iconStyle={breadcrumb.iconStyle}
                fixedWidth
            />
            <Text
                span
                size="sm"
                truncate
            >
                {breadcrumb.label}
            </Text>
        </Group>
    </Tooltip>
);

/**
 * Orders and filters breadcrumb registrations for responsive navigation.
 *
 * Postconditions: Loading or unlabeled entries are omitted and adjacent equal href/label pairs
 * are deduplicated after ordering. If the deepest route breadcrumb matches the pathname, entries
 * at order 100 or above survive only when their href matches. Mobile groups middle entries in a menu.
 */
const ModernBreadcrumbs = () => {
    const { pathname } = useLocation();
    const { breadcrumbs = [] } = useContext(LayoutContext);

    const resolved = useMemo(
        () => {
            const ordered = breadcrumbs
                .filter(item => !item.loading && item.label)
                .sort((left, right) => left.order - right.order);

            const unique = ordered.filter((item, index) =>
                index === 0 ||
                item.href !== ordered[index - 1].href ||
                item.label !== ordered[index - 1].label
            );
            const deepestStructural = unique.findLast(item => item.code.startsWith("route:"));

            return deepestStructural?.href === pathname
                ? unique.filter(item => item.order < 100 || item.href === pathname)
                : unique;
        },
        [breadcrumbs, pathname]
    );

    if (resolved.length === 0) {
        return null;
    }

    /**
     * Renders noncurrent entries as links only when an href exists; current entries remain text.
     */
    const renderCrumb = (item: ILayoutBreadcrumb, current: boolean) =>
        !current && item.href ? (
            <Anchor
                key={item.code}
                component={RouterLink}
                to={item.href}
                c="var(--mantine-color-text)"
                underline="hover"
            >
                <BreadcrumbContent breadcrumb={item} />
            </Anchor>
        ) : (
            <Text
                key={item.code}
                component="span"
                c={current ? "dimmed" : undefined}
                aria-current={current ? "page" : undefined}
            >
                <BreadcrumbContent breadcrumb={item} />
            </Text>
        );

    const first = resolved[0];
    const last = resolved.at(-1)!;
    const middle = resolved.slice(1, -1);

    return (
        <>
            <Box
                component="nav"
                aria-label="Breadcrumb"
                bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-8))"
                px="md"
                py="xs"
            >
                <HelpTourTarget id={helpTourTargets.breadcrumbs}>
                    <Breadcrumbs
                        visibleFrom="sm"
                        separator={
                            <Icon
                                icon={AppSettings.icons.arrowRight}
                                size="xs"
                            />
                        }
                    >
                        {resolved.map((item, index) =>
                            renderCrumb(item, index === resolved.length - 1)
                        )}
                    </Breadcrumbs>
                </HelpTourTarget>
                <Breadcrumbs
                    hiddenFrom="sm"
                    separator={
                        <Icon
                            icon={AppSettings.icons.arrowRight}
                            size="xs"
                        />
                    }
                >
                    {renderCrumb(first, resolved.length === 1)}
                    {middle.length > 0 && (
                        <Menu
                            position="bottom-start"
                            withinPortal
                        >
                            <Menu.Target>
                                <Anchor
                                    component="button"
                                    type="button"
                                    c="var(--mantine-color-text)"
                                    aria-label="Show intermediate breadcrumbs"
                                >
                                    <Icon icon={AppSettings.icons.menu} />
                                </Anchor>
                            </Menu.Target>
                            <Menu.Dropdown>
                                {middle.map(item => (
                                    <Menu.Item
                                        key={item.code}
                                        component={item.href ? RouterLink : "div"}
                                        to={item.href}
                                        leftSection={
                                            <BreadcrumbIcon
                                                icon={item.icon}
                                                iconStyle={item.iconStyle}
                                                fixedWidth
                                            />
                                        }
                                    >
                                        {item.label}
                                    </Menu.Item>
                                ))}
                            </Menu.Dropdown>
                        </Menu>
                    )}
                    {resolved.length > 1 && renderCrumb(last, true)}
                </Breadcrumbs>
            </Box>
            <Divider />
        </>
    );
};

export default ModernBreadcrumbs;
